// Lab 1 deterministic scenarios: every required trace from the plan,
// including the dropped-response acceptance case.
import { defaultConfig } from './model';

const INVOKE = (client, command) => ({
  op: 'submit',
  to: client,
  command,
  label: 'invoke',
});
const CMD = (client) => ({ op: 'deliver', select: { kind: 'clientCommand', to: client } });
const REQUEST = (client, server) => ({
  op: 'deliver',
  select: { kind: 'message', from: client, to: server, msgType: 'request' },
});
const REPLY = (server, client) => ({
  op: 'deliver',
  select: { kind: 'message', from: server, to: client, msgType: 'reply' },
});
const RETRY = (client, seq) => ({
  op: 'fire',
  select: { kind: 'timer', to: client, timerKey: 'retry:' + seq },
});
const PUT = { op: 'put', key: 'x', value: 1 };

export function normalRpc() {
  return {
    config: defaultConfig(),
    seed: 1,
    decisions: [
      INVOKE('C1', PUT),
      CMD('C1'),
      REQUEST('C1', 'S'),
      REPLY('S', 'C1'),
    ],
  };
}

export function droppedRequest() {
  return {
    config: defaultConfig(),
    seed: 2,
    decisions: [
      INVOKE('C1', PUT),
      CMD('C1'),
      { ...REQUEST('C1', 'S'), op: 'drop' },
      RETRY('C1', 1),
      REQUEST('C1', 'S'),
      REPLY('S', 'C1'),
    ],
  };
}

// Phase 2 acceptance case: execute, lose the reply, retry the same identity,
// and get the cached result without a second application effect.
export function droppedReplyCached() {
  return {
    config: defaultConfig(),
    seed: 7,
    decisions: [
      INVOKE('C1', PUT),
      CMD('C1'),
      REQUEST('C1', 'S'),
      { ...REPLY('S', 'C1'), op: 'drop' },
      RETRY('C1', 1),
      REQUEST('C1', 'S'),
      REPLY('S', 'C1'),
    ],
  };
}

export function duplicateRequest() {
  return {
    config: defaultConfig(),
    seed: 3,
    decisions: [
      INVOKE('C1', PUT),
      CMD('C1'),
      { ...REQUEST('C1', 'S'), op: 'duplicate' },
      REQUEST('C1', 'S'),
      REQUEST('C1', 'S'),
      REPLY('S', 'C1'),
      REPLY('S', 'C1'),
    ],
  };
}

export function staleReplyIgnored() {
  return {
    config: defaultConfig(),
    seed: 5,
    decisions: [
      INVOKE('C1', PUT),
      CMD('C1'),
      REQUEST('C1', 'S'),
      { ...REPLY('S', 'C1'), op: 'duplicate' },
      REPLY('S', 'C1'),
      REPLY('S', 'C1'),
      INVOKE('C1', { op: 'put', key: 'y', value: 2 }),
      CMD('C1'),
      REQUEST('C1', 'S'),
      REPLY('S', 'C1'),
    ],
  };
}

export function repeatedRetries() {
  const decisions = [INVOKE('C1', PUT), CMD('C1')];
  for (let i = 0; i < 3; i++) {
    decisions.push({ ...REQUEST('C1', 'S'), op: 'drop' });
    decisions.push(RETRY('C1', 1));
  }
  decisions.push(REQUEST('C1', 'S'), REPLY('S', 'C1'));
  return { config: defaultConfig(), seed: 9, decisions };
}

export function multiClient() {
  return {
    config: { ...defaultConfig(), clients: ['C1', 'C2'] },
    seed: 11,
    decisions: [
      INVOKE('C1', PUT),
      INVOKE('C2', { op: 'put', key: 'z', value: 9 }),
      CMD('C1'),
      CMD('C2'),
      REQUEST('C2', 'S'),
      REQUEST('C1', 'S'),
      REPLY('S', 'C1'),
      REPLY('S', 'C2'),
    ],
  };
}

export const ALL_SCENARIOS = {
  normalRpc,
  droppedRequest,
  droppedReplyCached,
  duplicateRequest,
  staleReplyIgnored,
  repeatedRetries,
  multiClient,
};
